import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence, animate } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { Icons } from '../Icons';
import { resolveBackendUrl } from '../../utils/backendUrl';
import SubscriptionUpgradeModal from '../SubscriptionUpgradeModal';

function useViewportWidth() {
  const [w, setW] = useState(typeof window !== 'undefined' ? window.innerWidth : 375);
  useEffect(() => {
    const handler = () => setW(window.innerWidth);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return w;
}

// ─── Design tokens ────────────────────────────────────────────────────────────

const MONO = '"Geist Mono", ui-monospace, monospace';
const SANS = '"Geist", "Inter", system-ui, sans-serif';
const EASE = [0.22, 1, 0.36, 1];

const GRAIN_SVG = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`;

const TIERS = [
  { min: 90, name: 'Elite',   color: '#818cf8' },
  { min: 76, name: 'Diamond', color: '#a78bfa' },
  { min: 61, name: 'Gold',    color: '#f59e0b' },
  { min: 41, name: 'Silver',  color: '#94a3b8' },
  { min: 21, name: 'Bronze',  color: '#fb923c' },
  { min: 0,  name: 'Rookie',  color: '#6b7280' },
];

const EARN_COLOR = '#22c55e';

const getTier = (score) => TIERS.find(t => score >= t.min) || TIERS[TIERS.length - 1];

function getGreeting() {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

const fmtDollars = (n) => {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000)     return `$${(n / 1_000).toFixed(1)}K`;
  return `$${n.toLocaleString()}`;
};

// ─── Period earnings computation ──────────────────────────────────────────────
// Returns sparkValues (cumulative within period) + pctChange vs previous period

function computePeriodData(deals, period) {
  if (!deals?.length) return { sparkValues: [], pctChange: null, changeDir: 'flat' };

  if (period === 'ALL') {
    let cum = 0;
    const sv = [...deals]
      .filter(d => d.deal_value && d.deal_date)
      .sort((a, b) => new Date(a.deal_date) - new Date(b.deal_date))
      .map(d => { cum += d.deal_value; return cum; });
    if (sv.length === 1) sv.unshift(0);
    return { sparkValues: sv, pctChange: null, changeDir: 'flat' };
  }

  const now      = new Date();
  const months   = period === '1M' ? 1 : period === '3M' ? 3 : 6;
  const curStart = new Date(now); curStart.setMonth(curStart.getMonth() - months);
  const prvStart = new Date(curStart); prvStart.setMonth(prvStart.getMonth() - months);

  const curDeals  = deals.filter(d => { const dt = new Date(d.deal_date); return dt >= curStart && dt <= now; });
  const prvDeals  = deals.filter(d => { const dt = new Date(d.deal_date); return dt >= prvStart && dt < curStart; });
  const curTotal  = curDeals.reduce((s, d) => s + (d.deal_value || 0), 0);
  const prvTotal  = prvDeals.reduce((s, d) => s + (d.deal_value || 0), 0);
  const pctChange = prvTotal > 0 ? ((curTotal - prvTotal) / prvTotal) * 100 : null;
  const changeDir = pctChange == null ? 'flat' : pctChange > 0 ? 'up' : pctChange < 0 ? 'down' : 'flat';

  let cum = 0;
  const sorted     = [...curDeals].sort((a, b) => new Date(a.deal_date) - new Date(b.deal_date));
  const sparkValues = [0, ...sorted.map(d => { cum += (d.deal_value || 0); return cum; })];
  if (sparkValues.length < 2) sparkValues.push(0);

  return { sparkValues, pctChange, changeDir };
}

// ─── LineMask ─────────────────────────────────────────────────────────────────
// Words slide up through a hidden overflow clip — the Apple/editorial reveal.

function LineMask({ children, delay = 0, style }) {
  return (
    <span style={{ display: 'block', overflow: 'hidden', paddingBottom: '0.04em', ...style }}>
      <motion.span
        initial={{ y: '108%' }}
        animate={{ y: '0%' }}
        transition={{ duration: 0.88, ease: EASE, delay }}
        style={{ display: 'block' }}
      >
        {children}
      </motion.span>
    </span>
  );
}

// ─── CountUp ──────────────────────────────────────────────────────────────────

function CountUp({ value, format, duration = 1.55 }) {
  const prevRef = useRef(null);
  const [display, setDisplay] = useState(format ? format(value) : String(value));

  useEffect(() => {
    if (value === prevRef.current) return;
    const from = prevRef.current;
    prevRef.current = value;
    // First mount: show value immediately, no count-up from zero
    if (from === null) {
      setDisplay(format ? format(value) : String(value));
      return;
    }
    const ctrl = animate(from, value, {
      duration,
      ease: EASE,
      onUpdate: (v) => setDisplay(format ? format(Math.round(v)) : String(Math.round(v))),
    });
    return ctrl.stop;
  }, [value, format, duration]);

  return <span>{display}</span>;
}

// ─── Score Arc ────────────────────────────────────────────────────────────────

function ScoreArc({ score, color, size = 128 }) {
  const sw = 7;
  const r  = (size - sw * 2) / 2;
  const cx = size / 2;
  const cy = size / 2;

  const sharedTransition = { duration: 1.65, ease: EASE, delay: 0.18 };

  return (
    <div style={{ position: 'relative', width: size, height: size }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)', overflow: 'visible' }}>
        {/* Track */}
        <circle
          cx={cx} cy={cy} r={r}
          fill="none"
          stroke="rgba(255,255,255,0.06)"
          strokeWidth={sw}
        />

        {/* Glow layer */}
        <motion.circle
          cx={cx} cy={cy} r={r}
          fill="none"
          stroke={color}
          strokeWidth={sw + 5}
          strokeLinecap="round"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: score / 100 }}
          transition={sharedTransition}
          style={{ opacity: 0.28, filter: 'blur(6px)' }}
        />

        {/* Crisp arc */}
        <motion.circle
          cx={cx} cy={cy} r={r}
          fill="none"
          stroke={color}
          strokeWidth={sw}
          strokeLinecap="round"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: score / 100 }}
          transition={sharedTransition}
          style={{ filter: `drop-shadow(0 0 5px ${color}99)` }}
        />
      </svg>

      {/* Center label */}
      <div style={{
        position: 'absolute', inset: 0,
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
      }}>
        <span style={{
          fontFamily: MONO,
          fontVariantNumeric: 'tabular-nums',
          fontFeatureSettings: '"tnum"',
          fontSize: size * 0.3,
          fontWeight: 700,
          color: '#fff',
          letterSpacing: '-0.04em',
          lineHeight: 1,
        }}>
          <CountUp value={score} duration={1.5} />
        </span>
        <span style={{
          fontFamily: SANS,
          fontSize: size * 0.095,
          fontWeight: 500,
          color: 'rgba(255,255,255,0.28)',
          letterSpacing: '0.07em',
          textTransform: 'uppercase',
          marginTop: 3,
        }}>
          / 100
        </span>
      </div>
    </div>
  );
}

// ─── Sparkline ────────────────────────────────────────────────────────────────

function Sparkline({ values, color = EARN_COLOR, width = 100, height = 46 }) {
  if (!values || values.length < 2) return null;

  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const pad = 5;

  const pts = values.map((v, i) => [
    pad + (i / (values.length - 1)) * (width - pad * 2),
    (height - pad) - ((v - min) / range) * (height - pad * 2),
  ]);

  const line = pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ');
  const last = pts[pts.length - 1];
  const area = `${line} L${last[0].toFixed(1)},${height} L${pts[0][0].toFixed(1)},${height} Z`;
  const gid  = `sp-${color.replace('#', '')}`;

  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} fill="none" style={{ overflow: 'visible' }}>
      <defs>
        <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.4" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      {/* Area fill fades in after line draws */}
      <motion.path
        d={area}
        fill={`url(#${gid})`}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.7, ease: EASE, delay: 0.75 }}
      />
      {/* Line draws left-to-right */}
      <motion.path
        d={line}
        stroke={color}
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
        initial={{ pathLength: 0 }}
        animate={{ pathLength: 1 }}
        transition={{ duration: 1.3, ease: EASE, delay: 0.38 }}
      />
      {/* Endpoint dot fades in after the line finishes drawing */}
      <motion.circle cx={last[0]} cy={last[1]} r="5" fill={color}
        initial={{ opacity: 0 }} animate={{ opacity: 0.2 }}
        transition={{ duration: 0.4, ease: EASE, delay: 1.25 }}
      />
      <motion.circle cx={last[0]} cy={last[1]} r="3" fill={color}
        initial={{ opacity: 0 }} animate={{ opacity: 1 }}
        transition={{ duration: 0.4, ease: EASE, delay: 1.25 }}
      />
    </svg>
  );
}

// ─── Spotlight card ───────────────────────────────────────────────────────────

function SpotlightCard({ accentColor, onClick, children, style }) {
  const ref    = useRef(null);
  const [mouse,   setMouse]   = useState({ x: 0, y: 0 });
  const [hovered, setHovered] = useState(false);

  const onMove = (e) => {
    const rect = ref.current?.getBoundingClientRect();
    if (rect) setMouse({ x: e.clientX - rect.left, y: e.clientY - rect.top });
  };

  return (
    <motion.div
      ref={ref}
      whileTap={{ scale: 0.978 }}
      onClick={onClick}
      onMouseMove={onMove}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{ position: 'relative', overflow: 'hidden', cursor: 'pointer', userSelect: 'none', ...style }}
    >
      {/* Grain texture — makes gradients feel physical, not digital */}
      <div style={{
        position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 0,
        opacity: 0.055, mixBlendMode: 'overlay',
        backgroundImage: GRAIN_SVG,
        backgroundSize: '120px 120px',
      }} />

      {/* Cursor spotlight */}
      <div style={{
        position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 1,
        opacity: hovered ? 1 : 0,
        transition: 'opacity 350ms ease',
        background: `radial-gradient(circle at ${mouse.x}px ${mouse.y}px,
          color-mix(in srgb, ${accentColor} 14%, transparent) 0%,
          transparent 62%)`,
      }} />

      {/* Top accent hairline — fades in from both sides */}
      <div style={{
        position: 'absolute', top: 0, left: '8%', right: '8%',
        height: 1, zIndex: 2, pointerEvents: 'none',
        background: `linear-gradient(90deg, transparent, ${accentColor}55 40%, ${accentColor}55 60%, transparent)`,
      }} />

      <div style={{ position: 'relative', zIndex: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
        {children}
      </div>
    </motion.div>
  );
}

// ─── Nudge bar ────────────────────────────────────────────────────────────────

function NudgeBar({ icon: Icon, message, cta, color, onClick }) {
  return (
    <motion.button
      initial={{ opacity: 0, y: -6 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -6 }}
      transition={{ duration: 0.5, ease: EASE }}
      whileTap={{ scale: 0.98 }}
      onClick={onClick}
      style={{
        width: '100%',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '11px 14px', borderRadius: 12,
        background: `color-mix(in srgb, ${color} 8%, #0F0F17)`,
        border: `1px solid color-mix(in srgb, ${color} 22%, rgba(255,255,255,0.06))`,
        borderLeft: `3px solid ${color}`,
        cursor: 'pointer', textAlign: 'left', gap: 10,
        fontFamily: SANS,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0 }}>
        <Icon style={{ width: 15, height: 15, color, flexShrink: 0 }} />
        <span style={{
          fontSize: '0.8rem', color: 'rgba(255,255,255,0.8)', fontWeight: 500,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {message}
        </span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: '0.72rem', color, fontWeight: 700, flexShrink: 0 }}>
        <span>{cta}</span>
        <Icons.ChevronRight style={{ width: 12, height: 12 }} />
      </div>
    </motion.button>
  );
}

// ─── Quick tile ───────────────────────────────────────────────────────────────

function QuickTile({ icon: Icon, color, label, sub, subColor, onClick }) {
  const [hovered, setHovered] = useState(false);

  return (
    <motion.button
      whileTap={{ scale: 0.92 }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={onClick}
      style={{
        width: '100%',
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        gap: 6, padding: '12px 6px', borderRadius: 14,
        background: hovered ? '#141421' : '#0F0F17',
        border: `1px solid ${hovered
          ? `color-mix(in srgb, ${color} 30%, rgba(255,255,255,0.08))`
          : 'rgba(255,255,255,0.06)'}`,
        cursor: 'pointer', minHeight: 80,
        transition: 'background 150ms ease, border-color 150ms ease, box-shadow 150ms ease',
        boxShadow: hovered ? `0 0 16px color-mix(in srgb, ${color} 10%, transparent)` : 'none',
        fontFamily: SANS,
      }}
    >
      <div style={{
        width: 34, height: 34, borderRadius: 10,
        background: `color-mix(in srgb, ${color} 16%, rgba(0,0,0,0))`,
        border: `1px solid color-mix(in srgb, ${color} 24%, transparent)`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        transition: 'transform 150ms ease',
        transform: hovered ? 'scale(1.08)' : 'scale(1)',
      }}>
        <Icon style={{ width: 17, height: 17, color }} />
      </div>
      <span style={{
        fontSize: '0.67rem', fontWeight: 600,
        color: hovered ? 'rgba(255,255,255,0.85)' : 'rgba(255,255,255,0.45)',
        letterSpacing: '0.01em', textAlign: 'center',
        transition: 'color 150ms ease',
      }}>
        {label}
      </span>
      {/* Always render — keeps all tiles same height; invisible when no sub */}
      <span style={{
        fontSize: '0.58rem', fontWeight: 700, fontFamily: MONO,
        color: sub ? (subColor || 'rgba(255,255,255,0.25)') : 'transparent',
        letterSpacing: '0.02em', textAlign: 'center',
        lineHeight: 1, minHeight: '0.8rem', display: 'block',
      }}>
        {sub || '·'}
      </span>
    </motion.button>
  );
}

// ─── Plan Section ─────────────────────────────────────────────────────────────

const PLAN_LIMITS = {
  free:  { contracts: 1,  captions: 2,  ghost: 0  },
  pro:   { contracts: 5,  captions: 10, ghost: 3  },
  elite: { contracts: -1, captions: -1, ghost: -1 },
};

const PLAN_COLORS = {
  free:  { bg: 'rgba(100,116,139,0.15)', text: '#94a3b8', border: 'rgba(100,116,139,0.25)' },
  pro:   { bg: 'rgba(90,56,253,0.15)',   text: '#a78bfa', border: 'rgba(90,56,253,0.3)'   },
  elite: { bg: 'rgba(245,158,11,0.13)',  text: '#fbbf24', border: 'rgba(245,158,11,0.28)' },
};

function PlanSection({ profile, userStats, onManage }) {
  const tier    = (userStats?.subscription_tier || profile?.subscription_tier || 'free').toLowerCase();
  const limits  = PLAN_LIMITS[tier] || PLAN_LIMITS.free;
  const colors  = PLAN_COLORS[tier] || PLAN_COLORS.free;
  const usage   = profile?.usage_counts || {};

  const rows = [
    { label: 'Contract Analyses',  icon: Icons.Contract, used: usage.contracts_this_month ?? 0,     limit: limits.contracts },
    { label: 'Caption Generations',icon: Icons.Content,  used: usage.captions_this_month ?? 0,      limit: limits.captions  },
    { label: 'Ghost Agent Chats',  icon: Icons.Ghost,    used: usage.ghost_agent_this_month ?? 0,   limit: limits.ghost     },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.52, duration: 0.55, ease: EASE }}
      style={{ marginTop: 20 }}
    >
      <div style={{
        background: '#0F0F17',
        border: '1px solid rgba(255,255,255,0.07)',
        borderRadius: 20, padding: '16px 18px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
            <Icons.Trophy style={{ width: 15, height: 15, color: colors.text }} />
            <span style={{ fontSize: '0.8rem', fontWeight: 700, color: 'rgba(255,255,255,0.75)' }}>Your Plan</span>
            <span style={{
              padding: '2px 8px', borderRadius: 6, fontSize: '0.6rem', fontWeight: 800,
              letterSpacing: '0.07em', textTransform: 'uppercase',
              background: colors.bg, color: colors.text, border: `1px solid ${colors.border}`,
            }}>{tier.toUpperCase()}</span>
          </div>
          <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={onManage}
            style={{
              padding: '5px 13px', borderRadius: 999, fontSize: '0.72rem', fontWeight: 700,
              background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
              color: 'rgba(255,255,255,0.55)', cursor: 'pointer', fontFamily: SANS,
            }}
          >Manage</motion.button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {rows.map(({ label, icon: Icon, used, limit }, idx) => {
            const pct = limit === -1 ? 1 : Math.min(used / Math.max(limit, 1), 1);
            const barColor = limit === -1 ? '#5A38FD' : pct > 0.85 ? '#f87171' : '#5A38FD';
            return (
              <div key={label}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 5 }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.77rem', color: 'rgba(255,255,255,0.45)', fontWeight: 500 }}>
                    <Icon style={{ width: 12, height: 12 }} />
                    {label}
                  </span>
                  <span style={{ fontSize: '0.7rem', fontFamily: MONO, fontWeight: 700, color: 'rgba(255,255,255,0.35)', fontVariantNumeric: 'tabular-nums' }}>
                    {used} / {limit === -1 ? '∞' : limit}
                  </span>
                </div>
                <div style={{ height: 3, borderRadius: 999, background: 'rgba(255,255,255,0.07)', overflow: 'hidden' }}>
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${(limit === -1 ? 1 : pct) * 100}%` }}
                    transition={{ duration: 0.9, ease: EASE, delay: 0.6 + idx * 0.08 }}
                    style={{ height: '100%', borderRadius: 999, background: barColor }}
                  />
                </div>
              </div>
            );
          })}
        </div>

        <p style={{ margin: '12px 0 0', fontSize: '0.65rem', color: 'rgba(255,255,255,0.18)' }}>
          Usage resets monthly.
        </p>
      </div>
    </motion.div>
  );
}

// Module-level cache — survives Dashboard unmount/remount on navigation
let _dashCache = { earnings: null };

function useScoreChangeBanner(nilScore) {
  const [banner, setBanner] = useState(null);
  const shown = useRef(false);

  useEffect(() => {
    if (!nilScore || shown.current) return;
    const prev = parseInt(localStorage.getItem('nil_score_prev') || '0', 10);
    if (prev > 0 && prev !== nilScore) {
      const delta = nilScore - prev;
      setBanner({ delta, prev, next: nilScore });
    }
    shown.current = true;
    localStorage.setItem('nil_score_prev', String(nilScore));
  }, [nilScore]);

  return [banner, () => setBanner(null)];
}

// ─── Dashboard ────────────────────────────────────────────────────────────────

const Dashboard = ({ setError, setActiveComponent, profile, userStats }) => {
  const { apiCall } = useAuth();
  const vw = useViewportWidth();

  const [earnings, setEarnings] = useState(
    _dashCache.earnings || { total: 0, active: 0, deals: [], loading: true }
  );
  const [earnPeriod, setEarnPeriod] = useState('1M');
  const [igStatus,         setIgStatus]         = useState({});
  const [igLoading,        setIgLoading]        = useState(true);
  const [draftPending,     setDraftPending]     = useState(false);
  const [missingContracts, setMissingContracts] = useState(0);
  const [showPlanModal,    setShowPlanModal]    = useState(false);
  const [expiryAlerts,     setExpiryAlerts]     = useState([]);
  const [complianceDoc,    setComplianceDoc]    = useState(null);
  const [complianceRegen,  setComplianceRegen]  = useState(false);

  const nilScore = userStats?.nil_score || profile?.nil_score || 0;
  const tier     = getTier(nilScore);
  const nav      = (id) => setActiveComponent?.(id);
  const [scoreBanner, dismissBanner] = useScoreChangeBanner(nilScore);

  // Scale hero elements with viewport — panels are 50% of constrained 680px container
  const arcSize   = vw >= 900 ? 152 : vw >= 600 ? 138 : 128;
  const sparkW    = vw >= 900 ? 130 : vw >= 600 ? 118 : 106;

  const fetchEarnings = useCallback(async () => {
    try {
      const res    = await apiCall('/api/earnings');
      const deals  = res?.deals || [];
      const total  = res?.totalEarnings ?? 0;
      const active  = deals.filter(d => d.status !== 'inactive').length;
      const missing = deals.filter(d => d.audit_status === 'missing_contract').length;

      const fresh = { total, active, deals, loading: false };
      _dashCache.earnings = fresh;
      setEarnings(fresh);
      setMissingContracts(missing);
    } catch {
      setEarnings(prev => ({ ...prev, loading: false }));
    }
  }, [apiCall]);

  const fetchDraftStatus = useCallback(async () => {
    try {
      const res = await apiCall('/api/ghost-agent/intake-draft/peek');
      setDraftPending(res?.has_draft === true);
    } catch {}
  }, [apiCall]);

  useEffect(() => {
    fetchEarnings();
    fetchDraftStatus();
    apiCall('/api/notifications?type=contract_expiry&read=false&limit=5')
      .then(res => setExpiryAlerts(res?.notifications || []))
      .catch(() => {});
    apiCall('/api/compliance/document')
      .then(res => setComplianceDoc(res?.exists ? res.doc : null))
      .catch(() => {});
    const cb = () => fetchEarnings();
    window.addEventListener('earnings:changed', cb);
    return () => window.removeEventListener('earnings:changed', cb);
  }, [fetchEarnings, fetchDraftStatus, apiCall]);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        setIgLoading(true);
        const res = await apiCall('/api/ig/status');
        if (!mounted) return;
        // Connected but followers never synced — hit /profile to pull from Instagram API
        if (res?.connected && !res?.followers_count) {
          const fresh = await apiCall('/api/ig/profile').catch(() => null);
          if (mounted) {
            setIgStatus({
              ...res,
              followers_count: fresh?.followers_count ?? 0,
              username: fresh?.username || res.username,
            });
          }
        } else if (mounted) {
          setIgStatus(res || {});
        }
        if (mounted) setIgLoading(false);
      } catch {
        if (mounted) { setIgStatus({ connected: false }); setIgLoading(false); }
      }
    };
    load();
    const t = setInterval(load, 60_000);
    return () => { mounted = false; clearInterval(t); };
  }, [apiCall]);


  const connectInstagram = () => {
    try {
      const backendUrl = resolveBackendUrl();
      const jwt     = localStorage.getItem('token') || '';
      const returnTo = `${window.location.origin}?ig=connected`;
      const url = `${backendUrl}/api/ig/oauth/start${jwt ? `?token=${encodeURIComponent(jwt)}` : ''}&return_to=${encodeURIComponent(returnTo)}`;
      window.location.assign(url);
    } catch { setError?.('Failed to start Instagram connection.'); }
  };

  // ── Derived ────────────────────────────────────────────────────────────────

  const profileCompletion = Math.round(profile?.profile_completion || 0);
  const periodData = computePeriodData(earnings.deals || [], earnPeriod);

  const nudge = draftPending
    ? { icon: Icons.Ghost,     color: '#7B63FD', message: 'Contract brief ready — Ghost Agent analyzed your latest deal', cta: 'Open',    onClick: () => nav('ghost')    }
    : missingContracts > 0
    ? { icon: Icons.Warning,   color: '#f59e0b', message: `${missingContracts} deal${missingContracts > 1 ? 's' : ''} missing a contract on file`, cta: 'Fix', onClick: () => nav('earnings') }
    : (!igLoading && !igStatus?.connected)
    ? { icon: Icons.Instagram, color: '#E1306C', message: 'Connect Instagram to unlock your full NIL Score', cta: 'Connect', onClick: connectInstagram }
    : null;

  const quickTiles = [
    { icon: Icons.Star,     color: '#5A38FD',  label: 'NIL Score',   id: 'nilscore', sub: nilScore > 0 ? `${nilScore}/100` : null,                                        subColor: tier.color       },
    { icon: Icons.Contract, color: '#5A38FD',  label: 'Contracts',   id: 'analyzer', sub: missingContracts > 0 ? `${missingContracts} missing` : null,                    subColor: '#fbbf24'        },
    { icon: Icons.Ghost,    color: '#5A38FD',  label: 'Ghost Agent', id: 'ghost',    sub: draftPending ? 'brief ready' : null,                                            subColor: '#a78bfa'        },
    { icon: Icons.Content,  color: '#5A38FD',  label: 'Posts',       id: 'captions' },
    { icon: Icons.Scanner,  color: '#5A38FD',  label: 'Scanner',     id: 'scanner'  },
    { icon: Icons.Earnings, color: '#5A38FD',  label: 'Earnings',    id: 'earnings', sub: earnings.active > 0 ? `${earnings.active} active` : null,                      subColor: '#4ade80'        },
  ];

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div style={{ padding: '20px 16px 0', fontFamily: SANS, maxWidth: 680, width: '100%', margin: '0 auto' }} className="nv-page-bottom-pad">

      {/* ── Score change banner ────────────────────────────────────────────── */}
      <AnimatePresence>
        {scoreBanner && (
          <motion.div
            initial={{ opacity: 0, y: -10, height: 0 }}
            animate={{ opacity: 1, y: 0, height: 'auto' }}
            exit={{ opacity: 0, y: -10, height: 0 }}
            transition={{ duration: 0.42, ease: EASE }}
            style={{ overflow: 'hidden', marginBottom: 12 }}
          >
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              gap: 10, padding: '11px 14px', borderRadius: 12,
              background: scoreBanner.delta > 0 ? 'rgba(34,197,94,0.08)' : 'rgba(239,68,68,0.08)',
              border: `1px solid ${scoreBanner.delta > 0 ? 'rgba(34,197,94,0.22)' : 'rgba(239,68,68,0.22)'}`,
              borderLeft: `3px solid ${scoreBanner.delta > 0 ? '#22c55e' : '#f87171'}`,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
                <Icons.Star style={{ width: 14, height: 14, color: scoreBanner.delta > 0 ? '#22c55e' : '#f87171', flexShrink: 0 }} />
                <span style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.8)', fontWeight: 500, fontFamily: SANS }}>
                  Your NIL Score {scoreBanner.delta > 0 ? 'went up' : 'dropped'}{' '}
                  <strong style={{ color: scoreBanner.delta > 0 ? '#4ade80' : '#f87171', fontFamily: MONO }}>
                    {scoreBanner.delta > 0 ? '+' : ''}{scoreBanner.delta}
                  </strong>
                  {' '}since your last visit
                  <span style={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.72rem', marginLeft: 6 }}>
                    ({scoreBanner.prev} → {scoreBanner.next})
                  </span>
                </span>
              </div>
              <button
                onClick={dismissBanner}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.3)', fontSize: '1rem', padding: '0 2px', lineHeight: 1, flexShrink: 0 }}
              >×</button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <LineMask
            delay={0.05}
            style={{
              fontSize: 'clamp(0.82rem, 3vw, 0.98rem)',
              fontWeight: 500,
              letterSpacing: '-0.005em',
              color: 'rgba(255,255,255,0.45)',
            }}
          >
            {getGreeting()},
          </LineMask>
          <LineMask
            delay={0.17}
            style={{
              fontSize: 'clamp(1.1rem, 4.2vw, 1.4rem)',
              fontWeight: 700,
              letterSpacing: '-0.025em',
              color: 'rgba(255,255,255,0.92)',
              marginTop: 1,
            }}
          >
            {profile?.first_name || 'Athlete'}
          </LineMask>
        </div>

        {/* Pulsing live dot */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.55, duration: 0.5 }}
          style={{ display: 'flex', alignItems: 'center', gap: 5, paddingTop: 5 }}
        >
          <motion.div
            animate={{ opacity: [1, 0.2, 1] }}
            transition={{ repeat: Infinity, duration: 2.4, ease: 'easeInOut' }}
            style={{ width: 6, height: 6, borderRadius: '50%', background: '#5A38FD' }}
          />
          <span style={{
            fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.07em',
            textTransform: 'uppercase', color: 'rgba(255,255,255,0.22)',
          }}>
            Live
          </span>
        </motion.div>
      </div>

      {/* ── Hero: Score + Earnings ─────────────────────────────────────────── */}
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.85, ease: EASE, delay: 0.1 }}
        style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}
      >

        {/* NIL Score panel */}
        <SpotlightCard
          accentColor={tier.color}
          onClick={() => nav('nilscore')}
          style={{
            borderRadius: 20,
            padding: '14px 12px 16px',
            background: `
              radial-gradient(ellipse 160% 130% at 80% 0%,
                color-mix(in srgb, ${tier.color} 42%, transparent) 0%,
                color-mix(in srgb, ${tier.color} 22%, transparent) 28%,
                color-mix(in srgb, ${tier.color} 9%, transparent) 55%,
                color-mix(in srgb, ${tier.color} 2%, transparent) 78%,
                transparent 100%),
              #08080D
            `,
            border: `1px solid color-mix(in srgb, ${tier.color} 30%, rgba(255,255,255,0.06))`,
            boxShadow: `
              0 0 0 1px rgba(255,255,255,0.03),
              0 4px 32px rgba(0,0,0,0.5),
              0 0 80px color-mix(in srgb, ${tier.color} 18%, transparent)
            `,
            minHeight: 210,
          }}
        >
          <span style={{
            fontSize: '0.57rem', fontWeight: 700, letterSpacing: '0.1em',
            color: 'rgba(255,255,255,0.28)', textTransform: 'uppercase',
            display: 'block', marginBottom: 12,
          }}>
            NIL Score
          </span>

          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <ScoreArc score={nilScore} color={tier.color} size={arcSize} />
          </div>

          <motion.div
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.5, ease: EASE }}
            style={{ display: 'flex', justifyContent: 'center', marginTop: 12 }}
          >
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              padding: '3px 9px', borderRadius: 20,
              background: `color-mix(in srgb, ${tier.color} 14%, rgba(0,0,0,0))`,
              border: `1px solid color-mix(in srgb, ${tier.color} 28%, transparent)`,
              fontSize: '0.59rem', fontWeight: 700, letterSpacing: '0.06em',
              color: tier.color, textTransform: 'uppercase',
            }}>
              <Icons.Trophy style={{ width: 8, height: 8 }} />
              {tier.name}
            </span>
          </motion.div>
        </SpotlightCard>

        {/* Earnings panel */}
        <SpotlightCard
          accentColor={EARN_COLOR}
          onClick={() => nav('earnings')}
          style={{
            borderRadius: 20,
            padding: '14px 14px 16px',
            background: `
              radial-gradient(ellipse 160% 130% at 20% 100%,
                color-mix(in srgb, ${EARN_COLOR} 28%, transparent) 0%,
                color-mix(in srgb, ${EARN_COLOR} 14%, transparent) 30%,
                color-mix(in srgb, ${EARN_COLOR} 5%, transparent) 60%,
                color-mix(in srgb, ${EARN_COLOR} 1%, transparent) 80%,
                transparent 100%),
              #08080D
            `,
            border: `1px solid color-mix(in srgb, ${EARN_COLOR} 22%, rgba(255,255,255,0.06))`,
            boxShadow: `
              0 0 0 1px rgba(255,255,255,0.03),
              0 4px 32px rgba(0,0,0,0.5),
              0 0 65px color-mix(in srgb, ${EARN_COLOR} 11%, transparent)
            `,
            minHeight: 210,
          }}
        >
          {/* Label row + % change badge */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <span style={{
              fontSize: '0.57rem', fontWeight: 700, letterSpacing: '0.1em',
              color: 'rgba(255,255,255,0.28)', textTransform: 'uppercase',
            }}>
              Earnings
            </span>
            <AnimatePresence mode="wait">
              {!earnings.loading && periodData.pctChange != null && (
                <motion.span
                  key={`${earnPeriod}-${periodData.changeDir}`}
                  initial={{ opacity: 0, y: -4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.25, ease: EASE }}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 2,
                    fontSize: '0.6rem', fontWeight: 800, fontFamily: MONO,
                    padding: '2px 6px', borderRadius: 5,
                    color: periodData.changeDir === 'up' ? '#4ade80' : periodData.changeDir === 'down' ? '#f87171' : 'rgba(255,255,255,0.3)',
                    background: periodData.changeDir === 'up' ? 'rgba(74,222,128,0.1)' : periodData.changeDir === 'down' ? 'rgba(248,113,113,0.1)' : 'rgba(255,255,255,0.04)',
                  }}
                >
                  {periodData.changeDir === 'up' ? '▲' : periodData.changeDir === 'down' ? '▼' : '—'}
                  {' '}{Math.abs(periodData.pctChange).toFixed(1)}%
                </motion.span>
              )}
            </AnimatePresence>
          </div>

          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <span style={{
              fontFamily: MONO,
              fontVariantNumeric: 'tabular-nums',
              fontFeatureSettings: '"tnum"',
              fontSize: 'clamp(1.55rem, 6.8vw, 2.4rem)',
              fontWeight: 700,
              color: '#fff',
              letterSpacing: '-0.035em',
              lineHeight: 1,
              display: 'block',
            }}>
              {earnings.loading
                ? <span style={{ color: 'rgba(255,255,255,0.25)' }}>—</span>
                : <CountUp value={earnings.total} format={fmtDollars} duration={1.5} />
              }
            </span>

            {periodData.sparkValues.length >= 2 && (
              <div style={{ marginTop: 14 }}>
                <Sparkline key={earnPeriod} values={periodData.sparkValues} color={EARN_COLOR} width={sparkW} height={42} />
              </div>
            )}
          </div>

          {/* Period chips — stopPropagation so card click doesn't navigate */}
          <div
            style={{ display: 'flex', gap: 4, marginTop: 12 }}
            onClick={e => e.stopPropagation()}
          >
            {['1M', '3M', '6M', 'ALL'].map(p => {
              const active = earnPeriod === p;
              return (
                <motion.button
                  key={p}
                  whileTap={{ scale: 0.92 }}
                  onClick={e => { e.stopPropagation(); setEarnPeriod(p); }}
                  style={{
                    padding: '3px 7px', borderRadius: 5, fontSize: '0.6rem',
                    fontWeight: 700, fontFamily: MONO, cursor: 'pointer', border: 'none',
                    background: active ? 'rgba(34,197,94,0.18)' : 'rgba(255,255,255,0.06)',
                    color: active ? '#4ade80' : 'rgba(255,255,255,0.28)',
                    transition: 'background 150ms, color 150ms',
                  }}
                >
                  {p}
                </motion.button>
              );
            })}
          </div>
        </SpotlightCard>
      </motion.div>

      {/* ── Social Media Section ───────────────────────────────────────────── */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.38, duration: 0.55, ease: EASE }}
        style={{ marginBottom: 12 }}
      >
        <div style={{
          position: 'relative', overflow: 'hidden',
          background: '#0F0F17', border: '1px solid rgba(255,255,255,0.07)',
          borderRadius: 18, padding: '14px 16px',
        }}>
          {/* Section header */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
            <span style={{ fontSize: '0.57rem', fontWeight: 700, letterSpacing: '0.1em', color: 'rgba(255,255,255,0.2)', textTransform: 'uppercase' }}>
              Social Media
            </span>
            {!igLoading && igStatus?.connected && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.6rem', fontWeight: 700, color: '#4ade80', letterSpacing: '0.05em' }}>
                <motion.div animate={{ opacity: [1, 0.3, 1] }} transition={{ repeat: Infinity, duration: 2.2, ease: 'easeInOut' }} style={{ width: 5, height: 5, borderRadius: '50%', background: '#4ade80' }} />
                Live
              </span>
            )}
          </div>

          {/* Instagram row */}
          <AnimatePresence mode="wait">
            {igLoading ? (
              <motion.div key="ig-loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 32, height: 32, borderRadius: 10, background: 'rgba(255,255,255,0.05)' }} />
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                    <div style={{ width: 80, height: 10, borderRadius: 4, background: 'rgba(255,255,255,0.06)' }} />
                    <div style={{ width: 54, height: 8, borderRadius: 4, background: 'rgba(255,255,255,0.04)' }} />
                  </div>
                </div>
                <div style={{ width: 48, height: 24, borderRadius: 6, background: 'rgba(255,255,255,0.04)' }} />
              </motion.div>
            ) : igStatus?.connected ? (
              <motion.div key="ig-connected" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease: EASE }}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}
                onClick={() => nav('scanner')}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 34, height: 34, borderRadius: 10, background: 'rgba(225,48,108,0.12)', border: '1px solid rgba(225,48,108,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Icons.Instagram style={{ width: 17, height: 17, color: '#E1306C' }} />
                  </div>
                  <div>
                    <div style={{ fontSize: '0.8rem', fontWeight: 700, color: 'rgba(255,255,255,0.88)', letterSpacing: '-0.01em' }}>
                      @{igStatus.username || profile?.instagram_handle || '—'}
                    </div>
                    <div style={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.3)', fontWeight: 500 }}>Instagram</div>
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontFamily: MONO, fontSize: '1.05rem', fontWeight: 700, color: '#fff', letterSpacing: '-0.02em', lineHeight: 1 }}>
                    <CountUp value={igStatus.followers_count || 0} duration={1.4} />
                  </div>
                  <div style={{ fontSize: '0.6rem', color: 'rgba(255,255,255,0.3)', fontWeight: 500, marginTop: 2 }}>followers</div>
                </div>
              </motion.div>
            ) : (
              <motion.div key="ig-disconnected" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease: EASE }}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 34, height: 34, borderRadius: 10, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Icons.Instagram style={{ width: 17, height: 17, color: 'rgba(255,255,255,0.2)' }} />
                  </div>
                  <div>
                    <div style={{ fontSize: '0.8rem', fontWeight: 600, color: 'rgba(255,255,255,0.4)' }}>Instagram</div>
                    <div style={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.22)' }}>Not connected</div>
                  </div>
                </div>
                <motion.button
                  whileTap={{ scale: 0.95 }}
                  onClick={connectInstagram}
                  style={{
                    padding: '6px 13px', borderRadius: 9, fontSize: '0.72rem', fontWeight: 700,
                    background: 'rgba(225,48,108,0.12)', border: '1px solid rgba(225,48,108,0.25)',
                    color: '#E1306C', cursor: 'pointer', fontFamily: SANS,
                  }}
                >
                  Connect
                </motion.button>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Profile completion bar */}
          {profileCompletion > 0 && profileCompletion < 100 && (
            <motion.div
              whileTap={{ scale: 0.99 }}
              onClick={() => nav('profile')}
              style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid rgba(255,255,255,0.05)', cursor: 'pointer' }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 5 }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: '0.7rem', fontWeight: 600, color: 'rgba(255,255,255,0.35)' }}>
                  <Icons.Profile style={{ width: 11, height: 11, color: 'rgba(255,255,255,0.3)' }} />
                  Profile completion
                </span>
                <span style={{ fontSize: '0.7rem', fontWeight: 700, fontFamily: MONO, color: '#a78bfa' }}>{profileCompletion}%</span>
              </div>
              <div style={{ height: 3, borderRadius: 999, background: 'rgba(255,255,255,0.06)', overflow: 'hidden' }}>
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${profileCompletion}%` }}
                  transition={{ duration: 0.85, ease: EASE, delay: 0.5 }}
                  style={{ height: '100%', borderRadius: 999, background: 'linear-gradient(90deg, #5A38FD, #7B63FD)' }}
                />
              </div>
            </motion.div>
          )}
        </div>
      </motion.div>

      {/* ── Nudge ──────────────────────────────────────────────────────────── */}
      <AnimatePresence>
        {nudge && (
          <motion.div
            key="nudge"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            style={{ overflow: 'hidden', marginBottom: 12 }}
          >
            <NudgeBar {...nudge} />
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Quick Access ───────────────────────────────────────────────────── */}
      <div>
        <LineMask
          delay={0.3}
          style={{
            fontSize: '0.57rem', fontWeight: 700, letterSpacing: '0.1em',
            color: 'rgba(255,255,255,0.2)', textTransform: 'uppercase',
            marginBottom: 10,
          }}
        >
          Quick Access
        </LineMask>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
          {quickTiles.map(({ icon, color, label, id, sub, subColor }, i) => (
            <motion.div
              key={id}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.34 + i * 0.07, duration: 0.55, ease: EASE }}
              style={{ width: '100%' }}
            >
              <QuickTile icon={icon} color={color} label={label} sub={sub} subColor={subColor} onClick={() => nav(id)} />
            </motion.div>
          ))}
        </div>
      </div>

      {/* ── Upcoming Contract Deadlines ────────────────────────────────────── */}
      <AnimatePresence>
        {expiryAlerts.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            transition={{ duration: 0.4, ease: EASE }}
            style={{ marginTop: 16 }}
          >
            <div style={{
              background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.2)',
              borderRadius: 16, padding: '1rem 1.125rem',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.875rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Icons.Clock style={{ width: 15, height: 15, color: '#f59e0b', flexShrink: 0 }} />
                  <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#f59e0b', letterSpacing: '0.06em', textTransform: 'uppercase', fontFamily: SANS }}>
                    Upcoming Deadlines
                  </span>
                </div>
                <button
                  onClick={() => nav('analyzer')}
                  style={{ fontSize: '0.72rem', color: '#f59e0b', background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontFamily: SANS, fontWeight: 600 }}
                >
                  Review →
                </button>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {expiryAlerts.slice(0, 3).map((alert, i) => {
                  const daysLeft = alert.data?.days_left;
                  const urgent = daysLeft != null && daysLeft <= 14;
                  return (
                    <div key={alert.id || i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                      <span style={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.75)', fontFamily: SANS, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {alert.title}
                      </span>
                      {daysLeft != null && (
                        <span style={{
                          fontSize: '0.68rem', fontWeight: 700, flexShrink: 0,
                          color: urgent ? '#f87171' : '#f59e0b',
                          background: urgent ? 'rgba(248,113,113,0.1)' : 'rgba(245,158,11,0.1)',
                          border: `1px solid ${urgent ? 'rgba(248,113,113,0.22)' : 'rgba(245,158,11,0.22)'}`,
                          borderRadius: 6, padding: '1px 7px', fontFamily: MONO,
                        }}>
                          {daysLeft}d
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Unaudited Deals Card ───────────────────────────────────────────── */}
      <AnimatePresence>
        {missingContracts > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            transition={{ duration: 0.4, ease: EASE, delay: 0.05 }}
            style={{ marginTop: 16 }}
          >
            <div
              onClick={() => nav('earnings')}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
                background: 'rgba(248,113,113,0.05)', border: '1px solid rgba(248,113,113,0.18)',
                borderRadius: 16, padding: '0.875rem 1.125rem', cursor: 'pointer',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 34, height: 34, borderRadius: 10, background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Icons.Warning style={{ width: 15, height: 15, color: '#f87171' }} />
                </div>
                <div>
                  <p style={{ margin: 0, fontSize: '0.82rem', fontWeight: 700, color: 'rgba(255,255,255,0.88)', fontFamily: SANS }}>
                    {missingContracts} deal{missingContracts > 1 ? 's' : ''} without a contract
                  </p>
                  <p style={{ margin: 0, fontSize: '0.72rem', color: 'rgba(255,255,255,0.38)', fontFamily: SANS }}>
                    Attach contracts to protect your earnings
                  </p>
                </div>
              </div>
              <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#f87171', fontFamily: SANS, flexShrink: 0 }}>Fix →</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Compliance Report Card ─────────────────────────────────────────── */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: EASE, delay: 0.1 }}
        style={{ marginTop: 16 }}
      >
        <div style={{
          background: 'rgba(90,56,253,0.05)', border: '1px solid rgba(90,56,253,0.18)',
          borderRadius: 16, padding: '1rem 1.125rem',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: complianceDoc ? '0.875rem' : 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Icons.Contract style={{ width: 15, height: 15, color: '#a78bfa', flexShrink: 0 }} />
              <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#a78bfa', letterSpacing: '0.06em', textTransform: 'uppercase', fontFamily: SANS }}>
                Compliance Report
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {complianceDoc && (
                <button
                  onClick={async () => {
                    try {
                      const token = localStorage.getItem('token');
                      const res = await fetch(`${resolveBackendUrl()}/api/compliance/document/download`, { headers: { Authorization: `Bearer ${token}` } });
                      const blob = await res.blob();
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a'); a.href = url; a.download = 'NIL_Compliance_Report.pdf'; a.click();
                      URL.revokeObjectURL(url);
                    } catch {}
                  }}
                  style={{ fontSize: '0.72rem', color: '#a78bfa', background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontFamily: SANS, fontWeight: 600 }}
                >
                  Download PDF
                </button>
              )}
              <button
                onClick={async () => {
                  if (complianceRegen) return;
                  setComplianceRegen(true);
                  try {
                    await apiCall('/api/compliance/document/regenerate', { method: 'POST' });
                    setTimeout(() => {
                      apiCall('/api/compliance/document').then(res => setComplianceDoc(res?.exists ? res.doc : null)).catch(() => {});
                      setComplianceRegen(false);
                    }, 4000);
                  } catch { setComplianceRegen(false); }
                }}
                style={{ fontSize: '0.72rem', color: complianceRegen ? 'rgba(255,255,255,0.28)' : '#a78bfa', background: 'none', border: 'none', cursor: complianceRegen ? 'default' : 'pointer', padding: 0, fontFamily: SANS, fontWeight: 600 }}
              >
                {complianceRegen ? 'Generating…' : complianceDoc ? 'Regenerate' : 'Generate'}
              </button>
            </div>
          </div>
          {complianceDoc ? (
            <div>
              <p style={{ margin: 0, fontSize: '0.82rem', fontWeight: 600, color: 'rgba(255,255,255,0.88)', fontFamily: SANS }}>
                Compliance Ready
              </p>
              <p style={{ margin: '2px 0 0', fontSize: '0.72rem', color: 'rgba(255,255,255,0.38)', fontFamily: SANS }}>
                v{complianceDoc.version} · Generated {new Date(complianceDoc.generated_at).toLocaleDateString()}
              </p>
            </div>
          ) : (
            <p style={{ margin: 0, fontSize: '0.8rem', color: 'rgba(255,255,255,0.45)', fontFamily: SANS }}>
              {complianceRegen ? 'Building your compliance report…' : 'No compliance report yet. Generate one to get a submission-ready PDF.'}
            </p>
          )}
        </div>
      </motion.div>

      {/* ── Your Plan ──────────────────────────────────────────────────────── */}
      <PlanSection profile={profile} userStats={userStats} onManage={() => setShowPlanModal(true)} />

      {showPlanModal && (
        <SubscriptionUpgradeModal
          isOpen={showPlanModal}
          onClose={() => setShowPlanModal(false)}
          currentTier={profile?.subscription_tier || 'free'}
        />
      )}
    </div>
  );
};

export default Dashboard;
